import { action, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import {
    ICreateHubMenuLinkDTO,
    IEventHubsDAO,
    IHubMenuLinkDTO,
    IHubPageDTO,
} from '../../../../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../../../../api/implementations/event-hubs.dao';
import { IReactIntlProvider } from '../../../../../../../infrastructure/react-intl-provider';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { SHORT_PLAIN_TEXT_LENGTH, MEDIUM_PLAIN_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import { getUrlError } from '../../../../../../../infrastructure/utils/form-validation.utils';
import { REQUIRED_ERROR_MESSAGE } from '../../../../../../../constants/validation-errors';

export interface IHubMenuLinkDraft {
    hubMenuLinkId: number | null;
    label: string;
    externalLink: string;
    enabled: boolean;
    positionOrder?: number;
}

const createEmptyDraft = (): IHubMenuLinkDraft => ({
    hubMenuLinkId: null,
    label: '',
    externalLink: '',
    enabled: true,
});

export class HubMenuSettingsVM {
    @observable
    private menuItems: (IHubPageDTO | IHubMenuLinkDTO)[] = [];
    @observable
    private draftLinks: IHubMenuLinkDTO[] = [];
    @observable
    private loading = false;
    @observable
    private saving = false;
    @observable
    private typePickerOpen = false;
    @observable
    private editingDraft: IHubMenuLinkDraft | null = null;
    @observable
    private labelTouched = false;
    @observable
    private urlTouched = false;

    private initialMenuItems: (IHubPageDTO | IHubMenuLinkDTO)[] = [];
    private createdLinkIds: Map<number, number> = new Map();

    private isLinkMenuItem = (item: IHubPageDTO | IHubMenuLinkDTO): item is IHubMenuLinkDTO => {
        return 'externalLink' in item && item.externalLink != null;
    };

    constructor(
        private readonly hubVersionId: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
    ) {
        void this.loadData();
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public isLoading(): boolean {
        return this.loading;
    }

    public isSaving(): boolean {
        return this.saving;
    }

    private hasReorderChanges(): boolean {
        const currentItems = this.menuItems.filter(item => this.isLinkMenuItem(item) || item.enabled);
        const initialItems = this.initialMenuItems.filter(item => this.isLinkMenuItem(item) || item.enabled);

        if (currentItems.length !== initialItems.length) {
            return true;
        }
        for (let i = 0; i < currentItems.length; i++) {
            const isCurrentLink = this.isLinkMenuItem(currentItems[i]);
            const isInitialLink = this.isLinkMenuItem(initialItems[i]);

            if (isCurrentLink && isInitialLink) {
                const currentLink = currentItems[i] as IHubMenuLinkDTO;
                const initialLink = initialItems[i] as IHubMenuLinkDTO;
                if (currentLink.hubMenuLinkId !== initialLink.hubMenuLinkId) {
                    return true;
                }
            } else if (!isCurrentLink && !isInitialLink) {
                const currentPage = currentItems[i] as IHubPageDTO;
                const initialPage = initialItems[i] as IHubPageDTO;
                if (currentPage.hubPageId !== initialPage.hubPageId) {
                    return true;
                }
            } else {
                return true;
            }
        }
        return false;
    }

    public hasUnsavedChanges(): boolean {
        return this.draftLinks.length > 0 || this.hasReorderChanges();
    }

    public isSaveButtonDisabled(): boolean {
        return !this.hasUnsavedChanges() || this.saving;
    }

    public isCancelButtonDisabled(): boolean {
        return !this.hasUnsavedChanges() || this.saving;
    }

    @action
    public onCancel = (): void => {
        this.draftLinks = [];
        this.createdLinkIds.clear();
        this.menuItems = [...this.initialMenuItems];
    };

    public onSaveChanges = (): void => {
        void this.saveAllDraftLinks();
    };

    public isTypePickerOpen(): boolean {
        return this.typePickerOpen;
    }

    @action
    public openTypePicker = (): void => {
        this.typePickerOpen = true;
    };

    @action
    public closeTypePicker = (): void => {
        this.typePickerOpen = false;
    };

    @action
    public onMenuItemTypeSelected = (): void => {
        this.closeTypePicker();
        this.openAddLinkModal();
    };

    public getMenuItems(): (IHubPageDTO | IHubMenuLinkDTO)[] {
        const draftLinkMap = new Map(this.draftLinks.map(link => [link.hubMenuLinkId, link]));

        const menuItemsWithDrafts = this.menuItems.reduce<(IHubPageDTO | IHubMenuLinkDTO)[]>((acc, item) => {
            if (this.isLinkMenuItem(item)) {
                const draft = draftLinkMap.get(item.hubMenuLinkId);
                acc.push(draft || item);
            } else if (item.enabled) {
                acc.push(item);
            }
            return acc;
        }, []);

        // Only append new draft links that aren't already in menuItems
        const menuItemIds = new Set(this.menuItems.map(item =>
            this.isLinkMenuItem(item) ? item.hubMenuLinkId : item.hubPageId
        ));
        const newDraftLinksNotInMenu = this.draftLinks.filter(link =>
            link.hubMenuLinkId < 0 && !menuItemIds.has(link.hubMenuLinkId)
        );

        return [...menuItemsWithDrafts, ...newDraftLinksNotInMenu];
    }

    public getDraftLinkIds(): Set<number> {
        return new Set(this.draftLinks.map(link => link.hubMenuLinkId));
    }

    public getEditingDraft(): IHubMenuLinkDraft | null {
        return this.editingDraft;
    }

    @action
    public openAddLinkModal = (): void => {
        this.editingDraft = createEmptyDraft();
        this.labelTouched = false;
        this.urlTouched = false;
    };

    @action
    public openEditLinkModal = (link: IHubMenuLinkDTO): void => {
        this.editingDraft = {
            hubMenuLinkId: link.hubMenuLinkId,
            label: link.label,
            externalLink: link.externalLink,
            enabled: link.enabled,
            positionOrder: link.positionOrder,
        };
        this.labelTouched = false;
        this.urlTouched = false;
    };

    @action
    public closeLinkModal = (): void => {
        this.editingDraft = null;
    };

    @action
    public setDraftLabel = (value: string): void => {
        if (this.editingDraft) {
            this.editingDraft.label = value;
        }
    };

    @action
    public setDraftUrl = (value: string): void => {
        if (this.editingDraft) {
            this.editingDraft.externalLink = value;
        }
    };

    @action
    public toggleDraftEnabled = (): void => {
        if (this.editingDraft) {
            this.editingDraft.enabled = !this.editingDraft.enabled;
        }
    };

    @action
    public markLabelTouched = (): void => {
        this.labelTouched = true;
    };

    @action
    public markUrlTouched = (): void => {
        this.urlTouched = true;
    };

    public getLabelError(): string | null {
        const label = this.editingDraft?.label ?? '';
        if (!label.trim()) {
            return this.labelTouched ? REQUIRED_ERROR_MESSAGE : null;
        }
        if (label.length > SHORT_PLAIN_TEXT_LENGTH) {
            return `This field allows ${SHORT_PLAIN_TEXT_LENGTH} characters max.`;
        }
        return null;
    }

    public getUrlValidationError(): string | null {
        const url = this.editingDraft?.externalLink ?? '';
        if (!url.trim()) {
            return this.urlTouched ? REQUIRED_ERROR_MESSAGE : null;
        }
        if (url.length > MEDIUM_PLAIN_TEXT_LENGTH) {
            return `This field allows ${MEDIUM_PLAIN_TEXT_LENGTH} characters max.`;
        }
        return getUrlError('URL', url);
    }

    public isSaveLinkDisabled(): boolean {
        const draft = this.editingDraft;
        if (!draft) {
            return true;
        }
        const hasLabel = !!draft.label.trim() && draft.label.length <= SHORT_PLAIN_TEXT_LENGTH;
        const hasValidUrl = !!draft.externalLink.trim()
            && draft.externalLink.length <= MEDIUM_PLAIN_TEXT_LENGTH
            && !getUrlError('URL', draft.externalLink);
        return this.saving || !hasLabel || !hasValidUrl;
    }

    public onSaveLink = (): void => {
        this.markLabelTouched();
        this.markUrlTouched();
        if (this.isSaveLinkDisabled()) {
            return;
        }
        this.addOrUpdateDraftLink();
    };

    @action
    private addOrUpdateDraftLink(): void {
        const draft = this.editingDraft;
        if (!draft) {
            return;
        }

        const isNew = draft.hubMenuLinkId == null;
        const payload: IHubMenuLinkDTO = {
            hubMenuLinkId: isNew ? -Date.now() : draft.hubMenuLinkId,
            label: draft.label.trim(),
            externalLink: draft.externalLink.trim(),
            enabled: draft.enabled,
        } as IHubMenuLinkDTO;

        if (isNew) {
            this.draftLinks = [...this.draftLinks, payload];
        } else {
            const existingIndex = this.draftLinks.findIndex(link => link.hubMenuLinkId === draft.hubMenuLinkId);
            if (existingIndex !== -1) {
                this.draftLinks[existingIndex] = payload;
            } else {
                this.draftLinks = [...this.draftLinks, payload];
            }
        }
        this.closeLinkModal();
    }

    @action
    public reorderMenuItems = (reorderedItems: (IHubPageDTO | IHubMenuLinkDTO)[]): void => {
        const draftLinkMap = new Map(this.draftLinks.map(d => [d.hubMenuLinkId, d]));

        // Build new menu items, using draft versions where they exist
        const newMenuItems: (IHubPageDTO | IHubMenuLinkDTO)[] = [];
        for (const item of reorderedItems) {
            if (this.isLinkMenuItem(item)) {
                const draft = draftLinkMap.get(item.hubMenuLinkId);
                newMenuItems.push(draft || item);
            } else {
                newMenuItems.push(item);
            }
        }

        // Get IDs of items that were reordered
        const reorderedIds = new Set(reorderedItems.map(item => {
            if (this.isLinkMenuItem(item)) {
                return item.hubMenuLinkId;
            }
            return item.hubPageId;
        }));

        // Preserve disabled items that weren't in the reorder (they stay at the end)
        const disabledItems = this.menuItems.filter(item => {
            if (this.isLinkMenuItem(item)) {
                const draft = draftLinkMap.get(item.hubMenuLinkId);
                const isDisabled = draft ? draft.enabled === false : item.enabled === false;
                return isDisabled && !reorderedIds.has(item.hubMenuLinkId);
            }
            return false;
        });

        // Map disabled items to drafts if they exist
        const disabledItemsWithDrafts = disabledItems.map(item => {
            if (this.isLinkMenuItem(item)) {
                const draft = draftLinkMap.get(item.hubMenuLinkId);
                return draft || item;
            }
            return item;
        });

        this.menuItems = [...newMenuItems, ...disabledItemsWithDrafts];
    };

    @action
    private setLoading(loading: boolean): void {
        this.loading = loading;
    }

    @action
    private setSaving(saving: boolean): void {
        this.saving = saving;
    }

    private async loadData(): Promise<void> {
        this.setLoading(true);
        try {
            const menuItems = await this.eventHubsDAO.getMenuItems(this.hubVersionId);
            this.setMenuItems(menuItems);
        } catch (error) {
            this.handleApiException(error);
        } finally {
            this.setLoading(false);
        }
    }

    @action
    private setMenuItems(items: (IHubPageDTO | IHubMenuLinkDTO)[]): void {
        this.menuItems = items;
        this.initialMenuItems = items;
    }

    @action
    private clearDraftLinksAndResets(): void {
        this.draftLinks = [];
        this.createdLinkIds.clear();
    }

    private async saveAllDraftLinks(): Promise<void> {
        if (this.draftLinks.length === 0 && !this.hasReorderChanges()) {
            return;
        }

        this.setSaving(true);
        try {
            const orderedItemIds = this.getMenuItems()
                .map(item => {
                    if (this.isLinkMenuItem(item)) {
                        return item.hubMenuLinkId;
                    }
                    return item.hubPageId;
                });

            if (this.draftLinks.length > 0) {
                for (const draftLink of this.draftLinks) {
                    const payload: ICreateHubMenuLinkDTO = {
                        label: draftLink.label,
                        externalLink: draftLink.externalLink,
                        enabled: draftLink.enabled,
                    };
                    if (draftLink.hubMenuLinkId < 0) {
                        const createdId = this.createdLinkIds.get(draftLink.hubMenuLinkId);
                        if (createdId === undefined) {
                            const created = await this.eventHubsDAO.createHubMenuLink(this.hubVersionId, payload);
                            this.createdLinkIds.set(draftLink.hubMenuLinkId, created.hubMenuLinkId);
                        } else {
                            await this.eventHubsDAO.updateHubMenuLink(createdId, payload);
                        }
                    } else {
                        await this.eventHubsDAO.updateHubMenuLink(draftLink.hubMenuLinkId, payload);
                    }
                }
            }

            const finalOrderedIds: number[] = orderedItemIds
                .map(id => {
                    if (id < 0) {
                        return this.createdLinkIds.get(id) || null;
                    }
                    return id;
                })
                .filter((id): id is number => id !== null && id > 0);

            const disabledPageIds: number[] = this.initialMenuItems
                .filter(item => !this.isLinkMenuItem(item) && !item.enabled)
                .map(item => (item as IHubPageDTO).hubPageId);

            if (this.hasReorderChanges() && finalOrderedIds.length > 0) {
                await this.eventHubsDAO.reorderMenuItems(this.hubVersionId, [...finalOrderedIds, ...disabledPageIds]);
            }

            await this.loadData();
            this.clearDraftLinksAndResets();
        } catch (error) {
            this.handleApiException(error);
        } finally {
            this.setSaving(false);
        }
    }

    private handleApiException(error: Error): void {
        const errorParsingService = Container.get(IErrorParsing);
        const apiError = errorParsingService.extractAPIError(error);
        const title = apiError?.errorCode === 403 ? 'Access Denied' : 'Error';
        alertServerError(title, errorParsingService.extractMessage(error));
    }
}
